import apiClient from './api';

class SubscriptionPlanService {
  /**
   * Get all subscription plans with optional filters
   * @param {Object} params - Query parameters {currency, is_active, subscription_billing_cycle, search, ordering, page}
   * @returns {Promise} - List of subscription plans
   */
  async getPlans(params = {}) {
    try {
      const response = await apiClient.get('/subscriptions/admin/plans/', { params });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
      return this.handleError(error);
    }
  }

  /**
   * Create subscription plan
   * @param {Object} data - Plan data
   * @returns {Promise} - Created subscription plan
   */
  async createPlan(data) {
    try {
      const response = await apiClient.post('/subscriptions/admin/plans/', data);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error creating subscription plan:', error);
      return this.handleError(error);
    }
  }

  /**
   * Get single subscription plan by ID
   * @param {string} id - Plan ID (UUID)
   * @returns {Promise} - Subscription plan details
   */
  async getPlanById(id) {
    try {
      const response = await apiClient.get(`/subscriptions/admin/plans/${id}/`);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching subscription plan:', error);
      return this.handleError(error);
    }
  }

  /**
   * Update subscription plan (full update)
   * @param {string} id - Plan ID (UUID)
   * @param {Object} data - Complete plan data
   * @returns {Promise} - Updated subscription plan
   */
  async updatePlan(id, data) {
    try {
      const response = await apiClient.put(`/subscriptions/admin/plans/${id}/`, data);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error updating subscription plan:', error);
      return this.handleError(error);
    }
  }

  /**
   * Partial update subscription plan
   * @param {string} id - Plan ID (UUID)
   * @param {Object} data - Partial plan data
   * @returns {Promise} - Updated subscription plan
   */
  async patchPlan(id, data) {
    try {
      const response = await apiClient.patch(`/subscriptions/admin/plans/${id}/`, data);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error patching subscription plan:', error);
      return this.handleError(error);
    }
  }

  /**
   * Delete subscription plan
   * @param {string} id - Plan ID (UUID)
   * @returns {Promise} - Success status
   */
  async deletePlan(id) {
    try {
      await apiClient.delete(`/subscriptions/admin/plans/${id}/`);

      return {
        success: true,
        message: 'Subscription plan deleted successfully',
      };
    } catch (error) {
      console.error('Error deleting subscription plan:', error);
      return this.handleError(error);
    }
  }

  /**
   * Toggle active status of subscription plan
   * @param {string} id - Plan ID (UUID)
   * @returns {Promise} - Updated subscription plan
   */
  async toggleActive(id) {
    try {
      const response = await apiClient.post(`/subscriptions/admin/plans/${id}/toggle_active/`);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error toggling subscription plan active status:', error);
      return this.handleError(error);
    }
  }

  /**
   * Sync plan from Razorpay (sync_from_razorpay_create)
   * @param {string} planId - Subscription plan ID (UUID)
   * @returns {Promise} - Synced plan data
   */
  async syncFromRazorpay(planId) {
    try {
      const response = await apiClient.post(
        `/subscriptions/admin/plans/${planId}/sync_from_razorpay/`
      );

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error syncing plan from Razorpay:', error);
      return this.handleError(error);
    }
  }

  /**
   * Fetch Razorpay plan details by Razorpay plan ID (retrieve from Razorpay and return plan data)
   * @param {string} razorpayPlanId - Razorpay plan ID (e.g. plan_xxx)
   * @returns {Promise} - Fetched plan data from Razorpay
   */
  async fetchRazorpayPlan(razorpayPlanId) {
    try {
      const response = await apiClient.get(
        '/subscriptions/admin/plans/fetch_razorpay_plan/',
        { params: { razorpay_plan_id: razorpayPlanId } }
      );

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching Razorpay plan:', error);
      return this.handleError(error);
    }
  }

  /**
   * Get only active plans
   * @returns {Promise} - List of active subscription plans
   */
  async getActivePlans() {
    try {
      const response = await apiClient.get('/subscriptions/admin/plans/active/');

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching active plans:', error);
      return this.handleError(error);
    }
  }

  /**
   * Handle API errors
   * @param {Error} error - Error object
   * @returns {Object} - Formatted error response
   */
  handleError(error) {
    let message = 'An unexpected error occurred';

    if (error.response) {
      if (error.response.data) {
        // Extract error message from response
        if (typeof error.response.data === 'string') {
          message = error.response.data;
        } else if (error.response.data.detail) {
          message = error.response.data.detail;
        } else if (error.response.data.error) {
          message = error.response.data.error;
        } else {
          // Try to get first error from field errors
          const firstKey = Object.keys(error.response.data)[0];
          if (firstKey && Array.isArray(error.response.data[firstKey])) {
            message = error.response.data[firstKey][0];
          } else if (firstKey && error.response.data[firstKey]) {
            message = `${firstKey}: ${error.response.data[firstKey]}`;
          }
        }
      }

      // Handle specific status codes
      if (error.response.status === 403) {
        message = 'Access denied. Admin privileges required.';
      } else if (error.response.status === 404) {
        message = 'Subscription plan not found.';
      }
    } else if (error.request) {
      message = 'Unable to connect to server. Please check your connection.';
    }

    return {
      success: false,
      error: message,
    };
  }
}

export default new SubscriptionPlanService();
